import Clickable from "./Clickable.mjs";
import Toggleable from "./Toggleable.mjs";
import {isObject, toQueryString} from "@spoferan/spoferan-ts-core";

export default {

    mixins: [Clickable, Toggleable],

    emits: ['click'],

    props: {
        // Where to navigate the user on a specific action. Might be a relative path or router object for the usage of Vue Router
        // or an absolute URL for the browser's native redirection.
        to: {
            type: [String, Object],
            default: null
        },

        // The browsers native 'target' attribute of a link
        target: {
            type: String,
            default: null
        },

        // The browsers native 'target' attribute of a link to append when the link is an absolute one
        absoluteTarget: {
            type: String,
            default: null
        },

        // Only sets the component's active state when the current route exactly matches the component's to prop
        exact: {
            type: Boolean,
            default: false
        },

        // Whether to call router.replace() instead of router.push() when clicked, so the navigation will not leave a history record.
        replace: {
            type: Boolean,
            default: false
        }
    },

    computed: {

        isClickable() {
            return !!(this.clickable || this.linkTo);
        },

        linkTo() {
            return this.to
        },

        // The relative path to navigate the user to a specific action
        relativeTo() {
            if (!this.linkTo) {
                return null;
            }

            const path = isObject(this.linkTo) ? (this.linkTo.path ?? this.linkTo.name) : this.linkTo;
            if (path && !path.startsWith('http') && !path.startsWith('www') && !path.startsWith('mailto')) {
                return this.linkTo;
            }

            return null;
        },

        // The absolute URL to navigate the user to a specific action
        absoluteTo() {
            if (this.linkTo && !this.relativeTo) {
                if (isObject(this.linkTo)) {
                    let url = this.linkTo.path;
                    if (this.linkTo.query && Object.keys(this.linkTo.query).length) {
                        url = `${url}?${toQueryString(this.linkTo.query)}`;
                    }
                    if (this.linkTo.hash && typeof this.linkTo.hash === 'string' && this.linkTo.hash.length) {
                        url = `${url}#${this.linkTo.hash}`;
                    }
                    
                    return url;
                }

                // We support misconfigured links starting without a protocol
                if (this.linkTo.startsWith('www')) {
                    return `https://${this.linkTo}`;
                }

                return this.linkTo;
            }

            return null;
        },

        hasActiveTo() {
            const targetTo = this.relativeTo && isObject(this.relativeTo) ? this.$router.resolve(this.relativeTo) : {
                path: this.relativeTo,
                fullPath: this.relativeTo,
            };

            // We do not compare the hash for exact route matches, as these are not relevant and would break our mobile profile page tab focus scrolling logic
            return !!(targetTo.path && this.$route && ((!this.exact && this.$route.path.startsWith(targetTo.path)) || (this.exact && this.$route.fullPath.split('#')[0] === targetTo.fullPath.split('#')[0])));
        },

        // Required as computed prop since we would overwrite the 'nuxt-link' 'href' attribute otherwise
        linkProps() {
            if (this.disabled) {
                return null;
            }

            if (this.relativeTo) {
                return {
                    to: this.relativeTo,
                    target: this.target,
                    replace: this.replace
                }
            }

            let absoluteTarget = this.absoluteTarget ? this.absoluteTarget : this.target;

            return {
                href: this.absoluteTo,
                target: absoluteTarget,
                rel: absoluteTarget === '_blank' ? 'noopener' : null
            }
        },
    },

    watch: {
        hasActiveTo(value) {
            // A specified model value prop should have priority
            this.isActive = this.modelValue || value;
        }
    },

    // Set the 'isActive' on mounted to propagate the change to dependent components
    mounted() {
        // Required to set the active state in relation to the active path to always refresh the active state when the path changes
        // {@see https://spoferan.atlassian.net/browse/DEV-576}
        if (this.relativeTo) {
            // A specified model value prop should have priority
            this.isActive = this.modelValue || this.hasActiveTo;
        }
    },

    methods: {
        handleClick(event) {
            if (!this.disabled) {
                this.isActive = !this.isActive;
                this.$emit('click', event)
            }
        }
    }
}
